import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  BookOpen,
  RefreshCw,
  Save,
  Sparkles,
  Target,
  X,
} from "lucide-react";
import EmptyState from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { API_URL } from "@/lib/api";

const API = API_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

function formatNumber(value, digits = 2, { suffix = "", sign = false } = {}) {
  if (value === null || value === undefined) {
    return "—";
  }
  const numeric = Number(value);
  const prefix = sign && numeric > 0 ? "+" : "";
  return `${prefix}${numeric.toFixed(digits)}${suffix}`;
}

function ScoreBlock({ title, block }) {
  const max = Math.max(...Object.values(block.distribution || {}), 1);
  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>
          Average {formatNumber(block.average, 1)} · {block.sample} scored trades
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          {[1, 2, 3, 4, 5].map((score) => (
            <div key={score} className="flex items-center gap-2">
              <span className="w-4 text-xs text-muted-foreground">{score}</span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary/60">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${((block.distribution?.[score] || 0) / max) * 100}%` }}
                />
              </div>
              <span className="w-6 text-right text-xs text-muted-foreground">
                {block.distribution?.[score] || 0}
              </span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
            <p className="font-medium text-destructive">Low (1-2)</p>
            <p className="text-muted-foreground">
              {block.low_scores.trades} trades ·{" "}
              {formatNumber(block.low_scores.expectancy_r, 2, { sign: true })}R
            </p>
          </div>
          <div className="rounded-lg border border-success/30 bg-success/5 px-3 py-2">
            <p className="font-medium text-success">High (4-5)</p>
            <p className="text-muted-foreground">
              {block.high_scores.trades} trades ·{" "}
              {formatNumber(block.high_scores.expectancy_r, 2, { sign: true })}R
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GroupTable({ title, labelKey, rows, emptyLabel }) {
  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">{labelKey === "setup" ? "Setup" : "Tag"}</th>
                  <th className="py-2 pr-3 font-medium">Trades</th>
                  <th className="py-2 pr-3 font-medium">Win %</th>
                  <th className="py-2 pr-3 font-medium">Expectancy R</th>
                  <th className="py-2 font-medium">P/L</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row[labelKey]} className="border-b border-border/60 last:border-0">
                    <td className="py-2 pr-3 font-medium">
                      {labelKey === "tag" ? (
                        <Badge variant="outline" className="border-destructive/40 bg-destructive/10 text-destructive">
                          {row[labelKey]}
                        </Badge>
                      ) : (
                        row[labelKey]
                      )}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{row.trades}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{formatNumber(row.win_rate, 1)}</td>
                    <td
                      className={`py-2 pr-3 font-medium ${
                        row.expectancy_r === null
                          ? "text-muted-foreground"
                          : row.expectancy_r >= 0
                          ? "text-success"
                          : "text-destructive"
                      }`}
                    >
                      {formatNumber(row.expectancy_r, 2, { sign: true })}
                    </td>
                    <td className="py-2 text-muted-foreground">{formatNumber(row.total_pnl, 2, { sign: true })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReviewCard({ currentStrategy, mode }) {
  const [weekOf, setWeekOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/review/weekly?strategy_id=${currentStrategy.id}&mode=${mode}&week_of=${weekOf}`,
        { headers: authHeaders(), timeout: 60000 }
      );
      setReview(response.data);
    } catch (error) {
      toast.error("Failed to generate the weekly review");
    } finally {
      setLoading(false);
    }
  }, [currentStrategy.id, mode, weekOf]);

  return (
    <Card className="shadow-sm" data-testid="review-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-primary" />
              Weekly review
            </CardTitle>
            <CardDescription>
              Numbers, mistakes and one focus for next week. Uses your local model when available.
            </CardDescription>
          </div>
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label htmlFor="review-week" className="text-xs">
                Any date in the week
              </Label>
              <Input
                id="review-week"
                type="date"
                value={weekOf}
                onChange={(event) => setWeekOf(event.target.value)}
                className="h-9 w-40"
              />
            </div>
            <Button onClick={generate} disabled={loading} data-testid="review-generate">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Generating…" : "Generate"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!review && (
          <p className="py-3 text-sm text-muted-foreground">
            No review generated yet for this strategy.
          </p>
        )}
        {review && (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                { label: "Trades", value: review.current.trades },
                { label: "P&L", value: formatNumber(review.current.total_pnl, 2, { sign: true }) },
                { label: "Win rate %", value: formatNumber(review.current.win_rate, 1) },
                { label: "Expectancy R", value: formatNumber(review.current.expectancy_r, 2, { sign: true }) },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border border-border bg-secondary/40 px-3 py-2">
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="text-sm font-semibold">{item.value}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Week {review.week_start} → {review.week_end} · vs previous week: P&L{" "}
              {formatNumber(review.deltas.total_pnl, 2, { sign: true })} · expectancy R{" "}
              {formatNumber(review.deltas.expectancy_r, 2, { sign: true })}
            </p>

            {review.narrative ? (
              <div className="whitespace-pre-line rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm leading-relaxed">
                {review.narrative}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                AI narrative disabled {review.ai?.error ? `(error: ${review.ai.error})` : "— set OLLAMA_URL to enable it"}.
              </p>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <GroupTable
                title="Setups this week"
                labelKey="setup"
                rows={review.top_setups}
                emptyLabel="No setups tagged this week."
              />
              <GroupTable
                title="Behaviour tags this week"
                labelKey="tag"
                rows={review.top_tags}
                emptyLabel="No behaviour tags this week."
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function PlaybookView({ currentStrategy, mode }) {
  const [playbook, setPlaybook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSetup, setNewSetup] = useState("");
  const [setups, setSetups] = useState([]);
  const [dailyMaxLoss, setDailyMaxLoss] = useState("");
  const [weeklyTarget, setWeeklyTarget] = useState("");
  const [maxRiskPct, setMaxRiskPct] = useState("");
  const [maxTradesPerDay, setMaxTradesPerDay] = useState("");

  const loadPlaybook = useCallback(async () => {
    if (!currentStrategy) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/playbook/${currentStrategy.id}?mode=${mode}`, {
        headers: authHeaders(),
      });
      setPlaybook(response.data);
      setSetups(response.data.setups || []);
      setDailyMaxLoss(response.data.daily_max_loss ? `${response.data.daily_max_loss}` : "");
      setWeeklyTarget(response.data.weekly_target ? `${response.data.weekly_target}` : "");
      setMaxRiskPct(response.data.max_risk_pct ? `${response.data.max_risk_pct}` : "");
      setMaxTradesPerDay(
        response.data.max_trades_per_day ? `${response.data.max_trades_per_day}` : ""
      );
    } catch (error) {
      toast.error("Failed to load the playbook");
    } finally {
      setLoading(false);
    }
  }, [currentStrategy, mode]);

  useEffect(() => {
    loadPlaybook();
  }, [loadPlaybook]);

  const addSetup = () => {
    const value = newSetup.trim();
    if (!value || setups.includes(value)) {
      return;
    }
    setSetups([...setups, value]);
    setNewSetup("");
  };

  const removeSetup = (setup) => {
    setSetups(setups.filter((item) => item !== setup));
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/strategies/${currentStrategy.id}/playbook?mode=${mode}`,
        {
          setups,
          daily_max_loss: dailyMaxLoss === "" ? 0 : parseFloat(dailyMaxLoss),
          weekly_target: weeklyTarget === "" ? 0 : parseFloat(weeklyTarget),
          max_risk_pct: maxRiskPct === "" ? 0 : parseFloat(maxRiskPct),
          max_trades_per_day: maxTradesPerDay === "" ? 0 : parseInt(maxTradesPerDay, 10),
        },
        { headers: authHeaders() }
      );
      toast.success("Playbook saved");
      setPlaybook((current) => ({
        ...current,
        setups: response.data.setups,
        daily_max_loss: response.data.daily_max_loss,
        weekly_target: response.data.weekly_target,
        max_risk_pct: response.data.max_risk_pct,
        max_trades_per_day: response.data.max_trades_per_day,
      }));
      setDailyMaxLoss(response.data.daily_max_loss ? `${response.data.daily_max_loss}` : "");
      setWeeklyTarget(response.data.weekly_target ? `${response.data.weekly_target}` : "");
      setMaxRiskPct(response.data.max_risk_pct ? `${response.data.max_risk_pct}` : "");
      setMaxTradesPerDay(
        response.data.max_trades_per_day ? `${response.data.max_trades_per_day}` : ""
      );
    } catch (error) {
      toast.error(error.response?.data?.detail?.[0]?.msg || "Failed to save the playbook");
    } finally {
      setSaving(false);
    }
  };

  const totals = playbook?.totals;
  const currency = currentStrategy?.currency || "";

  const setupRows = playbook?.by_setup || [];

  if (!currentStrategy) {
    return (
      <div className="p-4 md:p-6" data-testid="playbook-no-strategy">
        <EmptyState
          icon={BookOpen}
          title="No strategy selected"
          description="Create or select a strategy to define its playbook and review your discipline."
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <Skeleton className="mb-2 h-9 w-52" />
        <Skeleton className="mb-6 h-4 w-80" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-64 rounded-xl lg:col-span-2" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="playbook-page">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold md:text-3xl">
          <BookOpen className="h-7 w-7 text-primary" />
          Playbook
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Setups, goals and discipline for {currentStrategy.name}.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="shadow-sm lg:col-span-1">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="h-4 w-4 text-primary" />
              Plan
            </CardTitle>
            <CardDescription>Setups you trade and your risk guardrails.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label>Setups</Label>
              <div className="flex flex-wrap gap-2">
                {setups.length === 0 && (
                  <p className="text-xs text-muted-foreground">No setups yet.</p>
                )}
                {setups.map((setup) => (
                  <Badge key={setup} variant="outline" className="border-primary/40 bg-primary/10 text-primary">
                    {setup}
                    <button
                      type="button"
                      className="ml-1"
                      onClick={() => removeSetup(setup)}
                      aria-label={`Remove ${setup}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <Input
                  value={newSetup}
                  placeholder="e.g. breakout"
                  onChange={(event) => setNewSetup(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addSetup();
                    }
                  }}
                  data-testid="playbook-new-setup"
                />
                <Button variant="outline" onClick={addSetup} data-testid="playbook-add-setup">
                  Add
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="daily-max-loss">Daily max loss ({currency})</Label>
              <Input
                id="daily-max-loss"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 200"
                value={dailyMaxLoss}
                onChange={(event) => setDailyMaxLoss(event.target.value)}
                data-testid="playbook-daily-loss"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="weekly-target">Weekly target ({currency})</Label>
              <Input
                id="weekly-target"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 500"
                value={weeklyTarget}
                onChange={(event) => setWeeklyTarget(event.target.value)}
                data-testid="playbook-weekly-target"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="max-risk-pct">Max risk per trade (% capital)</Label>
                <Input
                  id="max-risk-pct"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="e.g. 1"
                  value={maxRiskPct}
                  onChange={(event) => setMaxRiskPct(event.target.value)}
                  data-testid="playbook-max-risk-pct"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max-trades-day">Max trades per day</Label>
                <Input
                  id="max-trades-day"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="e.g. 3"
                  value={maxTradesPerDay}
                  onChange={(event) => setMaxTradesPerDay(event.target.value)}
                  data-testid="playbook-max-trades-day"
                />
              </div>
            </div>
            <Button
              className="w-full"
              onClick={savePlan}
              disabled={saving}
              data-testid="playbook-save"
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Saving…" : "Save plan"}
            </Button>

            {totals && (
              <div className="grid grid-cols-2 gap-3 border-t border-border pt-4 text-xs">
                <div>
                  <p className="text-muted-foreground">Trades</p>
                  <p className="font-semibold">{totals.trades}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Expectancy R</p>
                  <p className="font-semibold">{formatNumber(totals.expectancy_r, 2, { sign: true })}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Setup tagged</p>
                  <p className="font-semibold">{formatNumber(playbook.discipline.with_setup_pct, 0, { suffix: "%" })}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Graded</p>
                  <p className="font-semibold">{formatNumber(playbook.discipline.with_grade_pct, 0, { suffix: "%" })}</p>
                </div>
              </div>
            )}

            {playbook.compliance && (
              <div className="space-y-2 border-t border-border pt-4 text-xs">
                <p className="font-medium text-foreground">Rule compliance</p>
                {playbook.compliance.risk_rule_compliance_pct !== null ? (
                  <p className="text-muted-foreground">
                    Risk ≤ {formatNumber(playbook.compliance.max_risk_pct, 2, { suffix: "%" })} (
                    {formatNumber(playbook.compliance.risk_limit_amount)} {currency}):{" "}
                    <span className="font-semibold text-foreground">
                      {formatNumber(playbook.compliance.risk_rule_compliance_pct, 1, { suffix: "%" })}
                    </span>{" "}
                    · {playbook.compliance.risk_rule_violations} violations
                  </p>
                ) : (
                  <p className="text-muted-foreground">Set a max risk per trade to track this rule.</p>
                )}
                {playbook.compliance.trades_per_day_compliance_pct !== null ? (
                  <p className="text-muted-foreground">
                    Max {playbook.compliance.max_trades_per_day} trades/day:{" "}
                    <span className="font-semibold text-foreground">
                      {formatNumber(playbook.compliance.trades_per_day_compliance_pct, 1, { suffix: "%" })}
                    </span>{" "}
                    · {playbook.compliance.days_over_max_trades} days over
                  </p>
                ) : (
                  <p className="text-muted-foreground">Set a max trades per day to track this rule.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6 lg:col-span-2">
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <GroupTable
              title="Performance by setup"
              labelKey="setup"
              rows={setupRows}
              emptyLabel="No trades yet."
            />
            <GroupTable
              title="Behaviour tags (mistakes)"
              labelKey="tag"
              rows={playbook.by_tag}
              emptyLabel="No tags yet. Tag mistakes when editing a trade."
            />
          </motion.div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ScoreBlock title="Execution grade" block={playbook.grade} />
            <ScoreBlock title="Emotion" block={playbook.emotion} />
          </div>
          <ReviewCard currentStrategy={currentStrategy} mode={mode} />
        </div>
      </div>
    </div>
  );
}
